import React, {useEffect} from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  FormErrorMessage,
  List,
  ListItem,
  ListIcon,
  useToast,
} from "@chakra-ui/react";
import { KeyRound, Check, X } from "lucide-react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSetPasswordMutation } from "@state/apiSlice/requests";

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
  confirmPassword: Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref("password")], "Passwords don't match"),
});

interface FormValues {
  password: string;
  confirmPassword: string;
}

export const SetPassword = () => {
  const initialValues: FormValues = {
    password: "",
    confirmPassword: "",
  };

  const requirements = [
    {
      text: "At least 8 characters",
      test: (password: string) => password.length >= 8,
    },
    {
      text: "At least one uppercase letter",
      test: (password: string) => /[A-Z]/.test(password),
    },
    {
      text: "At least one lowercase letter",
      test: (password: string) => /[a-z]/.test(password),
    },
    {
      text: "At least one number",
      test: (password: string) => /[0-9]/.test(password),
    },
    {
      text: "At least one special character",
      test: (password: string) => /[^A-Za-z0-9]/.test(password),
    },
  ];

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      navigate("/", { replace: true });
    }
  }, [token, navigate]);

  if (!token) return null; 

  const [setPassword] = useSetPasswordMutation();

  const handleSubmit = async (values: FormValues, { setSubmitting }: any) => {
    try {
      const result = await setPassword({password: values.password, token}).unwrap();

      if (result.success) {
        toast({
          title: "Password Set Succcessfully",
          status: "success",
          duration: 3000,
          position: "bottom-right",
        });
      
        navigate('/')
      }
    } catch (err) {
      toast({
        title: "Error setting password",
        description: "Please try again later",
        status: "error",
        duration: 5000,
        position: "bottom-right",
      });
    } finally {
      setSubmitting(false)
    }
    
  };

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" p={4}>
      <VStack
        spacing={8}
        w="full"
        maxW="400px"
        bg="gray.800"
        p={8}
        borderRadius="lg"
        boxShadow="xl"
        border="1px"
        borderColor="gray.700"
      >
        <Heading size="lg" color="white">
          Set Your Password
        </Heading>
        <Text color="gray.400" textAlign="center">
          Create a strong password to secure your account.
        </Text>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, isSubmitting }) => (
            <Form
              style={{
                width: "100%",
              }}
            >
              <VStack spacing={6} align="stretch">
                <Field name="password">
                  {({ field }: any) => (
                    <FormControl isInvalid={Boolean(errors.password && touched.password)}>
                      <FormLabel color="gray.300">Password</FormLabel>
                      <Input {...field} type="password" placeholder="••••••••" bg="gray.700" border="none" color="white" />
                      <FormErrorMessage>{errors.password}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Box>
                  <Text color="gray.400" fontSize="sm" mb={2}>
                    Password Requirements:
                  </Text>
                  <List spacing={1}>
                    {requirements.map((req, index) => (
                      <ListItem
                        key={index}
                        color={req.test(values.password) ? "green.400" : "gray.400"}
                        fontSize="sm"
                        display="flex"
                        alignItems="center"
                      >
                        <ListIcon
                          as={req.test(values.password) ? Check : X}
                          color={req.test(values.password) ? "green.400" : "gray.500"}
                        />
                        {req.text}
                      </ListItem>
                    ))}
                  </List>
                </Box>
                <Field name="confirmPassword">
                  {({ field }: any) => (
                    <FormControl isInvalid={Boolean(errors.confirmPassword && touched.confirmPassword)}>
                      <FormLabel color="gray.300">Confirm Password</FormLabel>
                      <Input {...field} type="password" placeholder="••••••••" bg="gray.700" border="none" color="white" />
                      <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Button
                  type="submit"
                  w="full"
                  bg="#ff3d00"
                  color="white"
                  leftIcon={<KeyRound size={20} />}
                  isLoading={isSubmitting}
                  loadingText="Setting Password..."
                  _hover={{
                    bg: "#e53700",
                    boxShadow: "0 0 20px rgba(255, 61, 0, 0.5)",
                  }}
                >
                  Set Password
                </Button>
              </VStack>
            </Form>
          )}
        </Formik>
      </VStack>
    </Box>
  );
};
